import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { aiPlanFilterFieldSx } from "../../../constants/regulationDetailsNavTabStyles";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { formatConceptCodeToLabel } from "../../../services/autoTaggingService";

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

const PLACEHOLDER = {
  objectType: "Select Object Type",
  objectSubType: "Select Object Sub-Type",
  concept: "Select Concept",
} as const;

function renderSelectPlaceholder(placeholder: string) {
  return (selected: string) => {
    if (!selected) {
      return (
        <Box component="span" sx={{ color: "#9ca3af" }}>
          {placeholder}
        </Box>
      );
    }
    return selected;
  };
}

function renderNamedValue(
  selected: string,
  options: Array<{ code: string; name: string }>,
  placeholder: string,
) {
  if (!selected) {
    return renderSelectPlaceholder(placeholder)("");
  }
  return options.find((option) => option.code === selected)?.name ?? selected;
}

function renderConceptValue(selected: string, placeholder: string) {
  if (!selected) {
    return renderSelectPlaceholder(placeholder)("");
  }
  return formatConceptCodeToLabel(selected);
}

type AssessmentPlanFilterFieldsProps = Readonly<{
  objectType: string;
  objectSubType: string;
  conceptCode: string;
  onObjectTypeChange: (objectType: string, objectSubType: string) => void;
  onObjectSubTypeChange: (objectSubType: string) => void;
  onConceptCodeChange: (conceptCode: string) => void;
  variant?: "filter" | "form";
  fullWidth?: boolean;
  errors?: Partial<{
    objectType: string;
    objectSubType: string;
    conceptCode: string;
  }>;
}>;

export default function AssessmentPlanFilterFields({
  objectType,
  objectSubType,
  conceptCode,
  onObjectTypeChange,
  onObjectSubTypeChange,
  onConceptCodeChange,
  variant = "filter",
  fullWidth = false,
  errors,
}: AssessmentPlanFilterFieldsProps) {
  const {
    objectTypeOptions,
    conceptOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
    conceptsLoading,
  } = useAssessmentPlanFilterOptions(objectType, objectSubType);

  const objectSubTypeOptions = objectType
    ? getObjectSubTypeOptions(objectType)
    : [];
  const isForm = variant === "form";
  // Always show Sub-Type by default; hide only when a type has no sub-types (e.g. RTQ).
  // Form: empty type still shows Sub-Type (disabled). Filter: same.
  const showObjectSubType = !objectType || objectSubTypeOptions.length > 0;
  const fieldSx = fullWidth ? { ...aiPlanFilterFieldSx, width: "100%" } : aiPlanFilterFieldSx;
  const subTypeDisabled = planTypesLoading || !objectType;

  const objectTypeSelect = (
    <FormControl
      fullWidth={fullWidth}
      size="small"
      error={Boolean(errors?.objectType)}
      sx={fieldSx}
    >
      <Select
        displayEmpty
        value={objectType}
        disabled={planTypesLoading}
        onChange={(event) => {
          // Maps to API `target_type`. Form preselects first `sub_type`; filter resets to All.
          const nextObjectType = event.target.value;
          if (!isForm) {
            onObjectTypeChange(nextObjectType, "");
            return;
          }
          const nextSubTypes = getObjectSubTypeOptions(nextObjectType);
          onObjectTypeChange(nextObjectType, nextSubTypes[0]?.code ?? "");
        }}
        renderValue={(selected) =>
          renderNamedValue(
            selected,
            objectTypeOptions,
            PLACEHOLDER.objectType,
          )
        }
      >
        {objectTypeOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
      {errors?.objectType ? (
        <FormHelperText>{errors.objectType}</FormHelperText>
      ) : null}
    </FormControl>
  );

  const objectSubTypeSelect = showObjectSubType ? (
    <FormControl
      fullWidth={fullWidth}
      size="small"
      error={Boolean(errors?.objectSubType)}
      sx={fieldSx}
    >
      <Select
        displayEmpty
        value={objectType ? objectSubType : ""}
        disabled={subTypeDisabled}
        onChange={(event) => onObjectSubTypeChange(event.target.value)}
        renderValue={(selected) =>
          renderNamedValue(
            selected,
            objectSubTypeOptions,
            PLACEHOLDER.objectSubType,
          )
        }
      >
        {objectSubTypeOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
      {errors?.objectSubType ? (
        <FormHelperText>{errors.objectSubType}</FormHelperText>
      ) : null}
    </FormControl>
  ) : null;

  const conceptSelect = (
    <FormControl
      fullWidth={fullWidth}
      size="small"
      error={Boolean(errors?.conceptCode)}
      sx={fieldSx}
    >
      <Select
        displayEmpty
        value={objectType ? conceptCode : ""}
        disabled={conceptsLoading || !objectType}
        onChange={(event) => onConceptCodeChange(event.target.value)}
        renderValue={(selected) =>
          renderConceptValue(selected, PLACEHOLDER.concept)
        }
      >
        {conceptOptions.map((option) => (
          <MenuItem key={option.code} value={option.code}>
            {formatConceptCodeToLabel(option.code)}
          </MenuItem>
        ))}
      </Select>
      {errors?.conceptCode ? (
        <FormHelperText>{errors.conceptCode}</FormHelperText>
      ) : null}
    </FormControl>
  );

  if (isForm) {
    return (
      <>
        <Box>
          <Typography sx={formFieldLabelSx}>
            Object Type
            <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
              *
            </Box>
          </Typography>
          {objectTypeSelect}
        </Box>
        {showObjectSubType ? (
          <Box>
            <Typography sx={formFieldLabelSx}>
              Object Sub-Type
              {objectType && objectSubTypeOptions.length > 0 ? (
                <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
                  *
                </Box>
              ) : null}
            </Typography>
            {objectSubTypeSelect}
          </Box>
        ) : null}
        <Box>
          <Typography sx={formFieldLabelSx}>
            Concept
            <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
              *
            </Box>
          </Typography>
          {conceptSelect}
        </Box>
      </>
    );
  }

  return (
    <>
      {objectTypeSelect}
      {objectSubTypeSelect}
      {conceptSelect}
    </>
  );
}
